import React, {
    useState, useEffect, useRef
} from 'react';

import {
    TransformWrapper, TransformComponent, useControls
} from "react-zoom-pan-pinch";
import {
    Map, Upload, ZoomIn, ZoomOut, Maximize, Layers, FileImage, Loader2, ChevronLeft, ChevronRight, Trash2
} from 'lucide-react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { formatImageUrl } from '../utils/urlHelper';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ViewerControls = () => {
    const { zoomIn, zoomOut, resetTransform } = useControls();
    return (
        <div className="absolute top-4 right-4 z-10 flex gap-2 bg-theme-bg/80 backdrop-blur-md p-2 rounded-xl border border-theme-border shadow-2xl pointer-events-auto">
            <button onClick={() => zoomIn()} className="p-2 hover:bg-theme-border text-theme-text-primary rounded-lg transition-colors" title="Acercar">
                <ZoomIn size={20} />
            </button>
            <button onClick={() => zoomOut()} className="p-2 hover:bg-theme-border text-theme-text-primary rounded-lg transition-colors" title="Alejar">
                <ZoomOut size={20} />
            </button>
            <div className="w-px bg-gray-700 mx-1"></div>
            <button onClick={() => resetTransform()} className="p-2 hover:bg-theme-border text-theme-text-primary rounded-lg transition-colors" title="Ajustar Pantalla">
                <Maximize size={20} />
            </button>
        </div>
    );
};

const BlueprintsViewer = () => {
    const [blueprints, setBlueprints] = useState([]);
    const [activeBlueprint, setActiveBlueprint] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    // Upload form states
    const [blueprintName, setBlueprintName] = useState('');
    const [blueprintVersion, setBlueprintVersion] = useState('v1.0');
    const fileInputRef = useRef(null);
    const [fileInput, setFileInput] = useState(null);

    // PDF Pagination states
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);

    const userRole = localStorage.getItem('userRole') || 'VIEWER';
    const isAdmin = userRole === 'COMPANY_ADMIN' || userRole === 'SUPER_ADMIN';

    const activeProjectName = localStorage.getItem('activeProjectName') || 'Proyecto Sin Asignar';

    // Reset pagination when active blueprint changes
    useEffect(() => {
        setPageNumber(1);
        setNumPages(null);
    }, [activeBlueprint]);
    useEffect(() => {
        fetchBlueprints();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const fetchBlueprints = async () => {
        try {
            setLoading(true);
            const projectId = localStorage.getItem('activeProjectId');
            const res = await axios.get('http://localhost:4000/api/blueprints', {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')
                        }`, 'x-project-id': projectId
                }
            });
            setBlueprints(res.data);
            if (res.data.length > 0 && !activeBlueprint) {
                setActiveBlueprint(res.data[0]);
            }
        } catch (error) {
            console.error("Error cargando planos:", error);
        } finally {
            setLoading(false);
        }
    };
    const handleFileUpload = async (e) => {
        e.preventDefault();
        if (!fileInput || !blueprintName) {
            alert("Selecciona un archivo y ponle un nombre.");
            return;
        } try {
            setUploading(true);
            const projectId = localStorage.getItem('activeProjectId');
            const formData = new FormData();
            formData.append('blueprint', fileInput);
            formData.append('name', blueprintName);
            formData.append('version', blueprintVersion);
            await axios.post('http://localhost:4000/api/blueprints/upload', formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')
                        }`, 'x-project-id': projectId, 'Content-Type': 'multipart/form-data'
                }
            });
            // Reset form and reload setFileInput(null);
            setBlueprintName('');
            setBlueprintVersion('v1.0');
            if (fileInputRef.current) fileInputRef.current.value = "";
            await fetchBlueprints();
        } catch (error) {
            console.error("Error subiendo plano:", error);
            alert("Error al subir el plano.");
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteBlueprint = async (id, name) => {
        if (!window.confirm(`¿Estás seguro que deseas eliminar el plano "${name}"?`)) return;
        try {
            const projectId = localStorage.getItem('activeProjectId');
            await axios.delete(`http://localhost:4000/api/blueprints/${id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'x-project-id': projectId
                }
            });
            
            // Si el plano eliminado era el activo, seleccionar otro
            if (activeBlueprint?.id === id) {
                const remaining = blueprints.filter(bp => bp.id !== id);
                setActiveBlueprint(remaining.length > 0 ? remaining[0] : null);
            }
            await fetchBlueprints();
        } catch (error) {
            console.error("Error al eliminar plano:", error);
            alert(error.response?.data?.error || "Error al eliminar el plano.");
        }
    };
    return (<div className="min-h-screen bg-theme-bg text-theme-text-primary flex flex-col font-sans"> {
        /* Header */
    } <div className="bg-theme-panel border-b border-theme-border p-4 md:px-8 py-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shrink-0"> <div> <h1 className="text-2xl font-extrabold tracking-tight text-theme-text-primary flex items-center gap-3"> <Map className="text-blue-500" size={
        28
    } /> Visor de Planos </h1> <p className="text-theme-text-secondary text-sm mt-1">Proyecto: {
        activeProjectName
    }</p> </div> </div> <div className="flex flex-col md:flex-row flex-1 overflow-hidden"> {
        /* Sidebar (List + Upload) */
    } <div className="w-full md:w-80 bg-theme-bg border-r border-theme-border p-4 flex flex-col gap-6 shrink-0 overflow-y-auto"> {
        /* Upload Section */
    } <form onSubmit={
        handleFileUpload
    } className="bg-theme-panel p-4 rounded-2xl border border-theme-border shrink-0"> <h3 className="font-bold text-sm text-theme-text-primary mb-3 flex items-center gap-2"> <Upload size={
        16
    } /> Subir Nuevo Plano </h3> <input type="text" placeholder="Nombre (ej. Planta Baja)" value={
        blueprintName
    } onChange={
        e => setBlueprintName(e.target.value)
    } className="w-full bg-theme-bg border border-theme-border rounded-lg px-3 py-2 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500 mb-2" /> <div className="flex gap-2 mb-3"> <input type="text" placeholder="Versión" value={
        blueprintVersion
    } onChange={
        e => setBlueprintVersion(e.target.value)
    } className="w-1/3 bg-theme-bg border border-theme-border rounded-lg px-3 py-2 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500" /> <input type="file" accept="image/*,application/pdf" ref={
        fileInputRef
    } onChange={
        e => setFileInput(e.target.files[0])
    } className="w-2/3 bg-theme-bg border border-theme-border rounded-lg px-3 py-1.5 text-xs text-theme-text-secondary file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-500 focus:outline-none" /> </div> <button type="submit" disabled={
        uploading || !fileInput || !blueprintName
    } className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-theme-text-primary rounded-lg py-2 text-sm font-bold transition-all flex justify-center items-center gap-2" > {
                            uploading ? <Loader2 size={
                                16
                            } className="animate-spin" /> : "Subir a Servidor"
                        } </button> </form> {
                    /* List Section */
                } <div className="flex-1 flex flex-col"> <div className="flex items-center gap-2 mb-3 text-theme-text-secondary px-1"> <Layers size={
                    18
                } /> <h3 className="font-bold text-sm">Archivos Disponibles</h3> </div> <div className="space-y-2 overflow-y-auto pr-1 pb-4"> {
                    loading && blueprints.length === 0 ? (<div className="flex justify-center p-4"><Loader2 className="animate-spin text-theme-text-secondary" /></div>) : blueprints.length === 0 ? (<div className="text-theme-text-secondary text-xs text-center p-4 bg-theme-panel rounded-xl border border-dashed border-theme-border">No hay planos registrados</div>) : (blueprints.map(bp => (
                        <div key={bp.id} className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${activeBlueprint?.id === bp.id ? 'bg-blue-600/20 border-blue-500/50' : 'bg-theme-panel border-theme-border hover:border-gray-500'}`}>
                            <button onClick={() => setActiveBlueprint(bp)} className="flex items-start gap-3 flex-1 text-left overflow-hidden">
                                <div className={`p-2 rounded-lg shrink-0 ${activeBlueprint?.id === bp.id ? 'bg-blue-600 text-theme-text-primary' : 'bg-gray-700 text-theme-text-secondary'}`}>
                                    <FileImage size={16} />
                                </div>
                                <div className="overflow-hidden">
                                    <h4 className={`font-bold text-sm truncate ${activeBlueprint?.id === bp.id ? 'text-blue-300' : 'text-gray-200'}`}>
                                        {bp.name}
                                    </h4>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="text-[10px] font-mono bg-theme-bg border border-theme-border px-1.5 py-0.5 rounded text-theme-text-secondary">
                                            {bp.version}
                                        </span>
                                        <span className="text-[10px] text-theme-text-secondary">
                                            {new Date(bp.uploaded_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            </button>
                            {isAdmin && (
                                <button 
                                    onClick={() => handleDeleteBlueprint(bp.id, bp.name)} 
                                    className="p-2 hover:bg-red-500/10 text-theme-text-secondary hover:text-red-400 rounded-lg transition-colors border border-transparent hover:border-red-500/20 ml-2 shrink-0"
                                    title="Eliminar Plano"
                                >
                                    <Trash2 size={16} />
                                </button>
                            )}
                        </div>)))
                } </div> </div> </div> {
                /* Main Viewer Area */
            } <div className="flex-1 bg-theme-bg relative flex flex-col overflow-hidden min-h-[500px]"> {
                !activeBlueprint ? (<div className="flex-1 flex flex-col items-center justify-center text-gray-600"> <Map size={
                    64
                } className="mb-4 opacity-50" /> <p className="font-bold text-lg">Ningún plano seleccionado</p> <p className="text-sm mt-1 text-center max-w-sm">Selecciona o sube un plano desde el panel lateral para visualizarlo en alta resolución.</p> </div>                ) : (
                    <>
                        <TransformWrapper key={activeBlueprint.id} initialScale={
                            1
                        } minScale={
                            0.5
                        } maxScale={
                            10
                        } wheel={
                            {
                                step: 0.1
                            }
                        } >
                            <ViewerControls />
                            <div className="absolute top-4 left-4 z-10 bg-theme-bg/80 backdrop-blur-md px-4 py-2 rounded-xl border border-theme-border shadow-2xl flex items-center gap-3 pointer-events-auto">
                                <span className="font-bold text-theme-text-primary shadow-sm">{activeBlueprint.name}</span>
                                <span className="text-xs font-mono bg-blue-600/30 text-blue-300 px-2 py-0.5 rounded border border-blue-500/30"> {activeBlueprint.version} </span>
                            </div>

                            <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }} contentStyle={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {activeBlueprint.file_path.toLowerCase().endsWith('.pdf') ? (
                                    <div className="max-w-none max-h-none drop-shadow-2xl">
                                        <Document 
                                            file={formatImageUrl(activeBlueprint.file_path)} 
                                            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                                            loading={<Loader2 size={32} className="animate-spin text-theme-text-secondary m-8" />}
                                            error={<div className="text-red-500 p-4 bg-red-500/10 rounded-lg border border-red-500/50">Error cargando PDF</div>}
                                        >
                                            <Page pageNumber={pageNumber} renderTextLayer={false} renderAnnotationLayer={false} />
                                        </Document>
                                    </div>
                                ) : (
                                    <img src={formatImageUrl(activeBlueprint.file_path)} alt={activeBlueprint.name} className="max-w-none max-h-none object-contain drop-shadow-2xl" />
                                )}
                            </TransformComponent>
                        </TransformWrapper>

                        {/* PDF Pagination Overlay Controls */}
                        {activeBlueprint.file_path.toLowerCase().endsWith('.pdf') && numPages && numPages > 1 && (
                            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-4 bg-theme-bg/85 backdrop-blur-md px-4 py-2 rounded-2xl border border-theme-border shadow-2xl pointer-events-auto select-none">
                                <button 
                                    type="button"
                                    onClick={() => setPageNumber(prev => Math.max(1, prev - 1))} 
                                    disabled={pageNumber <= 1}
                                    className="p-1.5 hover:bg-theme-border text-theme-text-primary rounded-lg transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                                >
                                    <ChevronLeft size={20} />
                                </button>
                                <span className="text-xs font-semibold text-theme-text-primary whitespace-nowrap">
                                    Página {pageNumber} de {numPages}
                                </span>
                                <button 
                                    type="button"
                                    onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))} 
                                    disabled={pageNumber >= numPages}
                                    className="p-1.5 hover:bg-theme-border text-theme-text-primary rounded-lg transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                        )}
                    </>
                )
            } </div> </div> </div >);
};
export default BlueprintsViewer;

